import { Box } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Anchor, FullScreenLoader, RightsTag } from "../../components";

import CompanyForm from "./components/CompanyForm";
import ProfileForm from "./components/ProfileForm";
import { styles } from "./style";
import Typography from "../../components/Typography/Typography";
import { BRAND, GREY } from "../../utils/constants/colors";
import { PRIVACY_POLICY_LINK } from "../../utils/constants/httpConstants";
import { STRINGS } from "../Auth/strings";
import { fontWeight, ICON_LABELS } from "../../utils/constants/UI";
import { TERMS_SERVICE_LINK } from "../../utils/constants";
// import BuyPackage from "../ProfileScreen/components/Settings/BuyPackage"; // "Choose Plan" step removed from onboarding
import { logoutUser } from "../../redux/actions/authActions";
import { LOGIN_PATH } from "../../utils/constants/routes";
import { Button } from "../../components/Buttons";
import SuspenseImage from "../../components/Image/SuspenseImage";
import {
  GREY_CIR,
  NEW_LOGIN_REACHIQ_LOGO,
  RED,
} from "../../utils/constants/assets";
import { postRequest } from "../../Apis/apiWrapper";
import { paymentEndpoints } from "../../utils/constants/httpConstants";
import { checkLogin } from "../../redux/actions/authActions";
import EventEmitter from "../../utils/emitter/EventEmitter";
import { EMITTER_ERROR } from "../../utils/constants";

const InitialForm = () => {
  const classes = styles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeStep, setActiveStep] = useState(-1); // -1 = not yet determined
  const [isFullLoading, setIsFullLoading] = useState(true);
  // Ref to ensure auto-detection only runs once
  const stepResolvedRef = useRef(false);
  const hasVerifiedPaymentRef = useRef(false);
  const [paymentState, setPaymentState] = useState(null); // 'verifying' | 'success' | 'cancelled' | null

  const userDetailsSelector = useSelector((state) => state?.userDetails);

  // 1. Aggressive Refresh: Always check for latest status on mount
  useEffect(() => {
    console.log("[InitialForm] Component mounted. Forcing data refresh...");
    dispatch(checkLogin());
  }, []);

  // ─── Step Resolution & Payment Verification ────────────────────────
  // Priority:
  //   1. Payment Verification (session_id in URL)
  //   2. React Router state (step override)
  //   3. Redux user data (auto-detection)
  // ────────────────────────────────────────────────────────────────────

  // Handle Stripe redirect params here directly in the form
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const isCancelled = params.get("payment_cancelled") === "true";

    if (isCancelled) {
      setPaymentState("cancelled");
      setActiveStep(0);
      setIsFullLoading(false);
      stepResolvedRef.current = true;
      window.history.replaceState({}, "", window.location.pathname);
    } else if (sessionId && !hasVerifiedPaymentRef.current) {
      verifyPaymentInternal(sessionId);
    }
  }, []);

  const verifyPaymentInternal = async (sessionId) => {
    try {
      setPaymentState("verifying");
      setIsFullLoading(true);
      hasVerifiedPaymentRef.current = true;

      const response = await postRequest(
        navigate,
        paymentEndpoints.verifySession,
        { session_id: sessionId },
      );

      if (response?.data?.status) {
        setPaymentState("success");
        // Clear session_id from URL
        window.history.replaceState({}, "", window.location.pathname);
        // Refresh Redux user data in background
        dispatch(checkLogin());
        // Auto-advance to Account Settings
        // setActiveStep(1); // OLD: index shifted after "Choose Plan" step removed
        setActiveStep(0); // Account Settings is now the first step
        stepResolvedRef.current = true;
      } else {
        setPaymentState(null);
        setActiveStep(0);
      }
    } catch (err) {
      console.error("Error verifying payment:", err);
      setPaymentState(null);
      setActiveStep(0);
      EventEmitter.emit(
        EMITTER_ERROR,
        err?.data?.message || "Payment verification failed.",
      );
    } finally {
      setIsFullLoading(false);
    }
  };

  // Check for step override from React Router navigate state (e.g. from logout or payment redirect)
  useEffect(() => {
    const navStep = location.state?.step;
    console.log("[InitialForm] Step override check:", {
      navStep,
      locationState: location.state,
    });
    if (navStep !== undefined && navStep !== null) {
      console.log("[InitialForm] Applying step override:", navStep);
      setActiveStep(navStep);
      setIsFullLoading(false);
      stepResolvedRef.current = true;
      // Clear the navigation state so a page refresh uses auto-detection
      window.history.replaceState({}, "", location.pathname);
    }
  }, [location.state, location.pathname]);

  // Auto-detect step from user data (only if not already resolved)
  useEffect(() => {
    console.log("[InitialForm] Auto-detect Status:", {
      stepResolved: stepResolvedRef.current,
      isLoading: userDetailsSelector?.loading,
      hasPayload: !!userDetailsSelector?.payload,
      activeStep,
    });

    if (stepResolvedRef.current) return;

    const payload = userDetailsSelector?.payload;
    const isLoading = userDetailsSelector?.loading;

    // Still loading user data - keep showing loader
    if (isLoading && !payload) return;

    // User data loaded - determine the step
    if (payload) {
      console.log(
        "[InitialForm] USER PAYLOAD:",
        JSON.stringify(payload, null, 2),
      );
      console.log("[InitialForm] Step Logic Check:", {
        has_subscription: payload.has_subscription,
        account_setting_setup: payload.account_setting_setup,
        general_setting_setup: payload.general_setting_setup,
      });

      // ─── OLD RESOLUTION (with "Choose Plan" step) ──────────────────
      // if (!payload.has_subscription) {
      //   console.log("[InitialForm] Resolution: Step 0 (Choose Plan)");
      //   setActiveStep(0); // Choose Plan
      // } else if (!payload.account_setting_setup) {
      //   console.log("[InitialForm] Resolution: Step 1 (Account Settings)");
      //   setActiveStep(1); // Account Settings
      // } else if (!payload.general_setting_setup) {
      //   console.log("[InitialForm] Resolution: Step 2 (User Profile)");
      //   setActiveStep(2); // User Profile
      // } else {
      //   console.log(
      //     "[InitialForm] Resolution: User fully setup, defaulting to Step 0",
      //   );
      //   setActiveStep(0);
      // }

      // ─── NEW RESOLUTION (Choose Plan removed) ──────────────────────
      if (!payload.account_setting_setup) {
        console.log("[InitialForm] Resolution: Step 0 (Account Settings)");
        setActiveStep(0); // Account Settings
      } else if (!payload.general_setting_setup) {
        console.log("[InitialForm] Resolution: Step 1 (User Profile)");
        setActiveStep(1); // User Profile
      } else {
        console.log(
          "[InitialForm] Resolution: User fully setup, defaulting to Step 0",
        );
        setActiveStep(0);
      }
      stepResolvedRef.current = true;
    }

    setIsFullLoading(false);
  }, [userDetailsSelector]);

  // 3. Subscription Watcher: If we are on Step 0 (Choose Plan) but Redux shows
  //    we have a sub, MOVE US. No longer needed now that "Choose Plan" is
  //    removed — Step 0 is Account Settings, where a subscribed user belongs.
  // useEffect(() => {
  //   const payload = userDetailsSelector?.payload;
  //   if (activeStep === 0 && payload?.has_subscription) {
  //     console.log(
  //       "[InitialForm] Subscription detected while on Step 0! Advancing...",
  //     );
  //     setActiveStep(1);
  //   }
  // }, [userDetailsSelector?.payload?.has_subscription, activeStep]);

  // ─── Step Transition ───────────────────────────────────────────────
  // This is passed as `setActiveStep` prop to child components.
  // BuyPackage calls it with 1, CompanyForm with 2, ProfileForm with 1.
  // ────────────────────────────────────────────────────────────────────

  const goToStep = (step) => {
    setActiveStep(step);
  };

  // ─── Handlers ──────────────────────────────────────────────────────

  const logoutHandler = () => {
    dispatch(logoutUser());
    navigate(LOGIN_PATH);
  };

  // ─── Steps Config ──────────────────────────────────────────────────

  // ─── OLD STEPS (with "Choose Plan") ────────────────────────────────
  // const steps = [
  //   {
  //     label: "Choose Plan",
  //     component: (
  //       <BuyPackage
  //         activeStep={activeStep}
  //         setActiveStep={goToStep}
  //         isOnboarding={true}
  //       />
  //     ),
  //   },
  //   {
  //     label: "Account Settings",
  //     component: (
  //       <CompanyForm activeStep={activeStep} setActiveStep={goToStep} />
  //     ),
  //   },
  //   {
  //     label: "User Profile",
  //     component: (
  //       <ProfileForm activeStep={activeStep} setActiveStep={goToStep} />
  //     ),
  //   },
  // ];

  // ─── NEW STEPS (Choose Plan removed) ───────────────────────────────
  const steps = [
    {
      label: "Account Settings",
      component: (
        <CompanyForm activeStep={activeStep} setActiveStep={goToStep} />
      ),
    },
    {
      label: "User Profile",
      component: (
        <ProfileForm activeStep={activeStep} setActiveStep={goToStep} />
      ),
    },
  ];

  if (paymentState === "verifying") {
    return (
      <FullScreenLoader label="Verifying your payment... please don't close this window." />
    );
  }

  if (isFullLoading || activeStep === -1) {
    return <FullScreenLoader />;
  }

  return (
    <Box className={classes.initialFormMainContainer}>
      {/* Header */}
      <Box
        className={classes.intitalFormHeader}
        style={{ padding: "16px 32px" }}
      >
        <SuspenseImage
          className={classes.logoIcon}
          src={NEW_LOGIN_REACHIQ_LOGO}
        />
        <Box className={classes.initialFormSteps}>
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <Box
                className={
                  activeStep > index
                    ? classes.stepLabelCompletedContainer
                    : activeStep === index
                      ? classes.stepLabelActiveContainer
                      : classes.stepLabelNotActiveContainer
                }
              >
                {activeStep === index && (
                  <SuspenseImage src={RED} className={classes.tickIcon} />
                )}
                {activeStep > index && (
                  <SuspenseImage src={RED} className={classes.tickIcon} />
                )}
                {activeStep < index && (
                  <SuspenseImage
                    src={GREY_CIR}
                    className={classes.tickIconred}
                  />
                )}
                <Typography
                  size={"md"}
                  color={activeStep === index ? BRAND.PRIMARY : GREY.TERTIARY}
                  fontWeight={
                    activeStep === index
                      ? fontWeight.MEDIUM
                      : fontWeight.REGULAR
                  }
                  label={step.label}
                />
              </Box>
              {index < steps.length - 1 && (
                <hr className={classes.horizontalLine} />
              )}
            </React.Fragment>
          ))}
        </Box>
        <Box className={classes.logoutContainer}>
          <Button
            LeftIcon={ICON_LABELS.LOGOUTS}
            label={STRINGS.LOGOUT}
            size={"sm36"}
            variant={"ghost"}
            onClick={logoutHandler}
          />
        </Box>
      </Box>

      {/* Steps Body */}
      <Box className={classes.stepsBodyMainContainer}>
        {steps[activeStep]?.component}
      </Box>

      <div className={classes.bottomContainer}>
        <div className={classes.containPolicy}>
          <RightsTag className={classes.termsPara} />
          <Anchor
            href={PRIVACY_POLICY_LINK}
            className={classes.privacyLink}
            target="_blank"
          >
            <div className={classes.hovering}>
              <Typography
                label={STRINGS.PRIVACY}
                color={GREY.QUATINARY}
                size={"xs"}
                fontWeight={fontWeight.SEMIBOLD}
              />
            </div>
          </Anchor>
          <div className={classes.TypoHead}>
            <Typography
              label={STRINGS.AND}
              color={GREY.TERTIARY}
              size={"xs"}
              fontWeight={fontWeight.REGULAR}
            />
          </div>
          <Anchor
            href={TERMS_SERVICE_LINK}
            className={classes.privacyLink}
            target="_blank"
          >
            <div className={classes.hovering}>
              <Typography
                label={STRINGS.TERMS}
                color={GREY.QUATINARY}
                size={"xs"}
                fontWeight={fontWeight.SEMIBOLD}
              />
            </div>
          </Anchor>
        </div>
      </div>
    </Box>
  );
};

export default InitialForm;
